import React, { useEffect } from 'react'
import { AppProps } from 'next/app'
import { useRouter } from 'next/router'
import Head from 'next/head'

import { NextIntlClientProvider } from 'next-intl'
import { ChakraProvider } from '@chakra-ui/react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'

import theme from '../themes'
import {
	buildBrandTheme,
	type BrandThemeTokens,
} from '../themes/buildBrandTheme'
import Layout from '../components/Layout'
import '@/components/RasaWebchat/RasaWebchat.css'
import '@/styles/flagFont.css'

import posthog from 'posthog-js'
import dynamic from 'next/dynamic'
import { GoogleTagManager } from '@next/third-parties/google'
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3'
import { RoleGuard } from '@/components/auth/RoleGuard'
import { CookieConsent } from '@/components/Landing/CookieConsent'
import tenant from '@/lib/tenant'
import { captureReferralCode } from '@/lib/referral'
import { DashboardSidebarProvider } from '@/lib/dashboardSidebarContext'
import { DEFAULT_TRANSLATIONS, mergeDeep } from '@/lib/defaultTranslations'
import { requestedLocale, type RuntimeLocalization } from '@/lib/localization'
import { LocalizationContext } from '@/lib/localizationContext'
import type { SiteNavigationData } from '@/lib/siteNavigation'

/*
 * Imported statically, NOT via next/dynamic with `ssr: false`.
 *
 * It was dynamic, and because <Component {...pageProps} /> is a CHILD of this
 * provider, that withheld the entire page tree from server rendering: a
 * dynamic component with ssr:false renders nothing on the server, children
 * included. Every page of every tenant therefore shipped an empty body —
 * measured at 668 bytes of markup on a live BYOD page, with zero <img> tags —
 * and nothing rendered, or began downloading, until the JS bundle had loaded,
 * parsed and hydrated. That is what made images appear late while scrolling:
 * the browser preload scanner had no URL to discover.
 *
 * Nothing required it to be dynamic. `posthog-js` itself is already imported
 * statically below, so the library has always run on the server; the React
 * provider is a plain context provider that renders its children untouched
 * (verified server-side before this change). Feature-flag hooks still resolve
 * to their defaults during SSR, which is what they already did.
 */
import { PostHogProvider } from 'posthog-js/react'

const RasaWebchat = dynamic(() => import('@/components/RasaWebchat'), {
	ssr: false,
})

export default function Crosure({ Component, pageProps }: AppProps) {
	const router = useRouter()
	const { locale } = router
	const routedLocale = requestedLocale({
		locale,
		query: router.query as Record<string, unknown>,
	})
	const hasExplicitLocale = typeof router.query.lang === 'string'
	const initialLocalization = (
		pageProps as {
			studioLocalization?: RuntimeLocalization
		}
	).studioLocalization
	const [runtimeLocalization, setRuntimeLocalization] =
		React.useState<RuntimeLocalization | null>(initialLocalization || null)
	const studioLocalization = hasExplicitLocale
		? runtimeLocalization
		: initialLocalization || runtimeLocalization
	// A full BCP-47 language can be runtime-configured even when Next's build-
	// time route list does not contain it. Preserve the active catalogue during
	// client-side navigation until `?lang=` is canonicalized onto the new URL.
	const routeLocale = hasExplicitLocale
		? routedLocale
		: router.locale && router.locale !== router.defaultLocale
			? routedLocale
			: studioLocalization?.locale || routedLocale
	const initialSiteNavigation = (
		pageProps as {
			siteNavigation?: SiteNavigationData
		}
	).siteNavigation
	const [runtimeSiteNavigation, setRuntimeSiteNavigation] = React.useState<
		SiteNavigationData | undefined
	>(initialSiteNavigation)
	const direction =
		studioLocalization?.direction ||
		(['ar', 'ckb', 'dv', 'fa', 'he', 'ku', 'ps', 'ur'].includes(
			routeLocale.split('-')[0]
		)
			? 'rtl'
			: 'ltr')
	const queryClient = new QueryClient()
	const [hasConsent, setHasConsent] = React.useState(false)

	// BYOD design system (Studio Tier 4.1) — loaded server-side by
	// loadThemeProps/loadBrandingProps. Null when the tenant has no design
	// system, in which case nothing is injected and the site renders as before.
	const pageDesignSystem = (
		pageProps as {
			designSystem?: {
				css?: string
				font_imports?: string[]
				favicon_url?: string
			} | null
		}
	)?.designSystem
	const [runtimeDesignSystem, setRuntimeDesignSystem] =
		React.useState(pageDesignSystem)
	const designSystem = runtimeDesignSystem
	const initialSiteTheme = (pageProps as { theme?: BrandThemeTokens })?.theme
	const [runtimeSiteTheme, setRuntimeSiteTheme] = React.useState<
		BrandThemeTokens | undefined
	>(initialSiteTheme)
	const siteTheme = runtimeSiteTheme
	const branding = (
		pageProps as {
			branding?: { primaryColor?: string; logoUrl?: string }
		}
	)?.branding
	// Site-wide logo from loadSiteNavigation() (pageProps.siteNavigation,
	// populated by getServerSideProps — see lib/sponsorPortalServerSideProps.ts
	// for the sponsor portal, pages/p/[slug].tsx for Page Builder pages) wins
	// when present; branding.logoUrl (a per-form override) wins over that for
	// the one page that set it. Both are optional — an unconfigured tenant
	// falls through to the tenant bundle's own logo, same as every other
	// field on this page.
	const siteNavLogo = runtimeSiteNavigation?.logoUrl
	const brandLogo = branding?.logoUrl || siteNavLogo
	// Generic, tenant-agnostic platform copy (dashboardSections etc. — see
	// lib/defaultTranslations.ts) merges UNDER whatever the active tenant
	// package provides, so a freshly scaffolded tenant with an empty
	// `translations: {}` still renders real text instead of next-intl's
	// raw-key fallback. A tenant's own wording always wins per-key.
	const bundledTranslations = mergeDeep(
		DEFAULT_TRANSLATIONS,
		pageProps.translations
	)
	const resolvedTranslations = mergeDeep(
		bundledTranslations,
		studioLocalization?.messages
	)
	const brandThemeTokens: BrandThemeTokens | undefined =
		siteTheme || branding?.primaryColor
			? {
					...siteTheme,
					...(branding?.primaryColor
						? { primaryColor: branding.primaryColor }
						: {}),
				}
			: undefined
	// Reconcile the BYOD CSS variables with the theme Chakra actually renders.
	// `design_system.css` carries RESOLVED tokens, so a colour the tenant never
	// overrode arrives as a platform default — while Chakra keeps the bundle's
	// own colour. Left alone, `var(--color-primary)` in custom CSS would not
	// match the buttons next to it. Emitting the effective colours after that
	// block makes the two agree.
	const effectiveTokenCss = React.useMemo(() => {
		if (!designSystem?.css || !brandThemeTokens) return ''
		const pairs: [string, string | undefined][] = [
			['--color-primary', brandThemeTokens.primaryColor],
			['--color-secondary', brandThemeTokens.secondaryColor],
			['--color-accent', brandThemeTokens.accentColor],
			['--color-success', brandThemeTokens.successColor],
			['--color-warning', brandThemeTokens.warningColor],
			['--color-destructive', brandThemeTokens.destructiveColor],
			['--font-heading', brandThemeTokens.fontHeading],
			['--font-body', brandThemeTokens.fontBody],
		]
		const decls = pairs
			.filter(([, v]) => typeof v === 'string' && v.trim())
			.map(([k, v]) => `  ${k}: ${v};`)
		return decls.length ? `:root {\n${decls.join('\n')}\n}` : ''
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [designSystem?.css, JSON.stringify(brandThemeTokens)])

	const activeTheme = React.useMemo(
		() => ({ ...buildBrandTheme(theme(), brandThemeTokens), direction }),
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[JSON.stringify(brandThemeTokens), direction]
	)
	// Override the nav logo with the tenant's when Studio provides one. The
	// marketing nav reads `logo`; the authenticated dashboard/sponsor-portal
	// Sidebar reads `logoWhite` — both point at the same Studio-configured
	// image (not `crosureLogo`, which is Crosure's own "powered by" mark and
	// stays a fixed platform asset, never tenant-configurable).
	const layoutTranslations = brandLogo
		? { ...resolvedTranslations, logo: brandLogo, logoWhite: brandLogo }
		: resolvedTranslations

	useEffect(() => {
		let active = true
		const pageLocalization = (
			pageProps as {
				studioLocalization?: RuntimeLocalization
			}
		).studioLocalization
		if (pageLocalization) setRuntimeLocalization(pageLocalization)
		const pageNavigation = (
			pageProps as {
				siteNavigation?: SiteNavigationData
			}
		).siteNavigation
		if (pageNavigation) setRuntimeSiteNavigation(pageNavigation)
		const pageTheme = (pageProps as { theme?: BrandThemeTokens }).theme
		if (pageTheme) setRuntimeSiteTheme(pageTheme)
		setRuntimeDesignSystem(pageDesignSystem)
		fetch(`/api/studio-localization?locale=${encodeURIComponent(routeLocale)}`)
			.then(response => (response.ok ? response.json() : Promise.reject()))
			.then(value => {
				if (active) setRuntimeLocalization(value as RuntimeLocalization)
			})
			.catch(() => undefined)
		fetch(
			`/api/studio-site-navigation?locale=${encodeURIComponent(routeLocale)}`
		)
			.then(response => (response.ok ? response.json() : Promise.reject()))
			.then(value => {
				if (active) setRuntimeSiteNavigation(value as SiteNavigationData)
			})
			.catch(() => undefined)
		fetch('/api/studio-theme')
			.then(response => (response.ok ? response.json() : Promise.reject()))
			.then(value => {
				if (!active) return
				const response = value as BrandThemeTokens & {
					designSystem?: typeof pageDesignSystem
				}
				setRuntimeSiteTheme(response)
				setRuntimeDesignSystem(response.designSystem)
			})
			.catch(() => undefined)
		return () => {
			active = false
		}
	}, [
		routeLocale,
		hasExplicitLocale,
		pageProps.studioLocalization,
		pageProps.siteNavigation,
		pageProps.theme,
		pageDesignSystem,
	])

	useEffect(() => {
		if (
			!router.isReady ||
			hasExplicitLocale ||
			!studioLocalization?.locale ||
			studioLocalization.locale === studioLocalization.default_locale
		) {
			return
		}
		void router.replace(
			{
				pathname: router.pathname,
				query: { ...router.query, lang: studioLocalization.locale },
			},
			undefined,
			{ shallow: true, locale: router.locale }
		)
	}, [
		router,
		hasExplicitLocale,
		studioLocalization?.locale,
		studioLocalization?.default_locale,
	])

	useEffect(() => {
		document.documentElement.lang = studioLocalization?.locale || routeLocale
		document.documentElement.dir = direction
	}, [direction, routeLocale, studioLocalization?.locale])

	useEffect(() => {
		// Tenants without a PostHog project configured shouldn't pay for a
		// pointless network call on every page load, and initializing with
		// an empty token just spams the console ("PostHog was initialized
		// without a token") without doing anything useful.
		const posthogKey = process.env.NEXT_PUBLIC_POSTHOG_KEY
		if (!posthogKey) return
		posthog.init(posthogKey, {
			api_host: process.env.NEXT_PUBLIC_POSTHOG_HOST,
			defaults: '2026-01-30',
		})
	}, [])

	// Referral capture — single choke point (referrals-plan.md §4/§7): read
	// `?ref=` once per visit and persist it so submitFormToStudio can attach
	// it to any later submission, on any entry page.
	useEffect(() => {
		captureReferralCode()
	}, [])

	// Check for consent (only for GTM state management)
	useEffect(() => {
		if (typeof window !== 'undefined') {
			const cookieConsent = localStorage.getItem('cookieConsent')

			// Set initial consent state (PostHog init handled by CookieConsent component)
			if (cookieConsent === 'true' && !hasConsent) {
				setHasConsent(true)
			}

			// DEBUG: Check for referral code cookie
			// const allCookies = document.cookie
			// const referralCookie = allCookies
			// 	.split(';')
			// 	.find(c => c.trim().startsWith('referralCode='))
			// if (referralCookie) {
			// 	// console.log('🎯 Referral cookie found:', referralCookie.trim())
			// } else if (router.query.ref) {
			// 	// console.log('⚠️ URL has ?ref=' + router.query.ref + ' but no cookie set yet')
			// }

			// Listen for immediate consent acceptance (same page)
			const handleConsentAccepted = () => {
				if (!hasConsent) {
					setHasConsent(true)
				}
			}

			// Listen for consent changes in other tabs
			const handleStorageChange = () => {
				const consent = localStorage.getItem('cookieConsent')
				if (consent === 'true' && !hasConsent) {
					setHasConsent(true)
				}
			}

			window.addEventListener('cookieConsentAccepted', handleConsentAccepted)
			window.addEventListener('storage', handleStorageChange)

			return () => {
				window.removeEventListener(
					'cookieConsentAccepted',
					handleConsentAccepted
				)
				window.removeEventListener('storage', handleStorageChange)
			}
		}
	}, [hasConsent])

	const siteTitle = runtimeSiteNavigation?.siteName || tenant.brand.appTitle
	const siteDescription =
		runtimeSiteNavigation?.tagline || tenant.brand.tagline || ''

	return (
		<>
			<Head>
				{/* Studio first, tenant bundle second. The description used to be
				    the literal "Protect your home and support your community" —
				    Commsure's copy served to every tenant, and unreachable by the
				    `branding.tagline` those tenants had already set in Studio.
				    runtimeSiteNavigation is hydrated from pageProps and refreshed
				    by the /api/studio-site-navigation fetch above, so pages that
				    don't load branding server-side still pick this up. */}
				<title>{siteTitle}</title>
				{siteDescription ? (
					<meta name='description' content={siteDescription} />
				) : null}
				{/* BYOD: the tenant's own fonts, design tokens and custom CSS.
				    `css` already has custom_css appended by Studio, so it goes in
				    as one block. Emitted last in <Head> so it wins over the
				    bundle's stylesheet on equal specificity. */}
				{designSystem?.font_imports?.map(href => (
					<link key={href} rel='stylesheet' href={href} />
				))}
				{/* Studio's design system first, then the tenant bundle's own, then
				    the platform file. Always emitted — an unconfigured tenant still
				    needs an icon, and `key` lets a page override it via next/head
				    rather than adding a second competing <link>. */}
				<link
					key='favicon'
					rel='icon'
					href={
						designSystem?.favicon_url ||
						tenant.brand.faviconHref ||
						'/favicon.ico'
					}
				/>
				{designSystem?.css ? (
					<style
						id='byod-design-tokens'
						// Tenant-authored CSS for the tenant's own storefront — the
						// point of BYOD. CSS cannot execute script.
						dangerouslySetInnerHTML={{ __html: designSystem.css }}
					/>
				) : null}
				{effectiveTokenCss ? (
					<style
						id='byod-effective-colors'
						dangerouslySetInnerHTML={{ __html: effectiveTokenCss }}
					/>
				) : null}
			</Head>
			<RoleGuard>
				<LocalizationContext.Provider
					value={
						studioLocalization || {
							locale: routeLocale,
							default_locale: routeLocale,
							supported_locales: [routeLocale],
							direction,
							messages: {},
							resources: {},
						}
					}
				>
					<NextIntlClientProvider
						locale={studioLocalization?.locale || routeLocale}
						messages={resolvedTranslations}
					>
						<ChakraProvider theme={activeTheme}>
							<QueryClientProvider client={queryClient}>
								<GoogleReCaptchaProvider
									reCaptchaKey={
										process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY || ''
									}
									scriptProps={{
										async: true,
										defer: true,
										appendTo: 'head',
									}}
								>
									<DashboardSidebarProvider>
										<Layout
											data={{
												...pageProps.schema,
												...layoutTranslations,
												// NavbarLanding hides itself entirely via `if (!data)
												// return null` when a page deliberately doesn't fetch
												// translations (see pages/dashboard/login.tsx,
												// pages/sponsor/login.tsx, forgot-password, reset-
												// password — all pre-auth pages with their own plain-
												// card layout, no public header). `layoutTranslations`
												// is never falsy — it's DEFAULT_TRANSLATIONS merged in
												// even when pageProps.translations is undefined — so
												// that guard must key off the raw, unmerged prop, not
												// the merged one, or every chrome-free page gets its
												// header/logo back.
												translations: pageProps.translations
													? layoutTranslations
													: undefined,
												siteNavigation: runtimeSiteNavigation,
												// A BYOD page renders the tenant's page-builder root,
												// which ships its own header and footer, so this
												// navbar would sit on top of the tenant's own.
												ownsChrome: pageProps.ownsChrome,
												// The tenant root's props for the form journey (Studio
												// page-shell). Present only when the tenant opted in;
												// Layout then renders the tenant's header there.
												shellRoot: pageProps.shellRoot,
												user: pageProps.user,
											}}
										>
											{process.env.NEXT_PUBLIC_ENVIRONMENT === 'prod' ? (
												<PostHogProvider client={posthog}>
													{hasConsent && (
														<GoogleTagManager
															gtmId={
																process.env.NEXT_PUBLIC_PROD_GTM_ID ??
																'GTM-WWX2LM6F'
															}
														/>
													)}

													<Component {...pageProps} />
												</PostHogProvider>
											) : (
												<PostHogProvider client={posthog}>
													<Component {...pageProps} />
												</PostHogProvider>
											)}
											<RasaWebchat
												locale={studioLocalization?.locale || routeLocale}
												route={router.asPath}
											/>
										</Layout>
									</DashboardSidebarProvider>
								</GoogleReCaptchaProvider>
								<CookieConsent />
							</QueryClientProvider>
						</ChakraProvider>
					</NextIntlClientProvider>
				</LocalizationContext.Provider>
			</RoleGuard>
		</>
	)
}
